import { Reducer } from 'redux';
import {
  AdditionalRequirement,
  AgentApplicantAuthorisationStatus,
  Application,
  DefaultTask,
  DropdownOptions,
  EmailStatus,
  ErrorResponseBody,
  GetStaffApplicationResponse,
  IApplicantOffer,
  IApplicantOfferRequirementTask,
  IApplyAuditLog,
  IOfferTimelineEntry,
  IApplyCourse,
  IEmail,
  IntakeOption,
  IUniId,
  RouteSection,
} from 'api-paths-cas';
import { ApplyActionTypes } from './types';
import { updateTaskInRequirements } from '@/utilities/transformRequirements';
import { CalendlySchedulingInfoResponse } from '@/api/api';

export interface CalendlySchedulingInfoState {
  isLoading: boolean;
  error?: unknown;
  data: CalendlySchedulingInfoResponse | null;
}
export interface IApplicantOfferWithUIState extends IApplicantOffer {
  changeStatusLoading?: boolean;
}

export interface IApplyState {
  applicant: GetStaffApplicationResponse['applicant'];
  application: Application;
  documentUrls: GetStaffApplicationResponse['documentUrls'];
  offers: IApplicantOffer[];
  applyAuditLogs: IApplyAuditLog[];
  offerTimeline: IOfferTimelineEntry[];
  applyEmails: IEmail[];
  offer?: IApplicantOfferWithUIState;
  additionalRequirements?: AdditionalRequirement[];
  requirementsConditionTexts?: {
    [key in string]: string;
  };
  courses?: IApplyCourse[];
  intakes?: DropdownOptions[];
  loadingIntakes?: boolean;
  courseDetails?: { programme: IApplicantOffer['programme']; requirements: string[] };
  offerCreated?: boolean;
  applicantIntakes?: IntakeOption[];
  loadingApplicantIntakes?: boolean;
  lastRegisteredApplicant?: {
    success: boolean;
    message: string;
    data: {
      uni?: string;
      universityId?: string;
      applicantEmail: string;
      preferredName: string;
      userId: string;
    };
  };
  loadingCourses?: boolean;
  loadingOffer?: boolean;
  loadingOfferId?: string;
  offerError?: string;
  offerErrorForOfferId?: string;
  agentAccessStatus: AgentApplicantAuthorisationStatus | null;

  registration: {
    agent: {
      showSuccessfullRegisterToast?: boolean;
      submittingRegisterApplicant?: boolean;
      registerApplicantError?: ErrorResponseBody | string;
    };
    uni: {
      showSuccessfullRegisterToast?: boolean;
      submittingRegisterApplicant?: boolean;
      registerApplicantError?: ErrorResponseBody | string;
    };
    partner: {
      showSuccessfullRegisterToast?: boolean;
      submittingRegisterApplicant?: boolean;
      registerApplicantError?: ErrorResponseBody | string;
    };
  };

  identityDocuments?: { name: string; urls: string[] }[];
  loadingIdentityDocuments?: boolean;
  sectionDocuments?: {
    [sectionType: string]: { name: string; urls: string[] }[];
  };
  loadingSectionDocuments?: {
    [sectionType: string]: boolean;
  };
  documentCounts?: {
    [docType: string]: number;
  };
  allDocuments?: {
    [docType: string]: { name: string; urls: string[] }[];
  };
  loadingAllDocuments?: boolean;
  isUpdatingApplicantDetails?: boolean;
  universityIds?: Array<IUniId>;
  applicantUpdateSuccess?: boolean;
  routeStatus: {
    sections: RouteSection[] | null;
    defaultTask: DefaultTask | null;
    isLoading: boolean;
    error?: string;
    loadedForApplicant?: string;
    loadedForApplication?: string;
  };
  updatedApplicationUserId?: string;
  updateDuplicateResponse?: boolean;
  updateDuplicateError?: string;
  calendlySchedulingInfos: Record<string, CalendlySchedulingInfoState | undefined>;
  selectStudiesToAssessSavedAt?: number;
}

const initialState: IApplyState = {
  applicant: {} as GetStaffApplicationResponse['applicant'],
  application: {} as Application,
  documentUrls: {},
  offers: [] as IApplicantOffer[],
  applyAuditLogs: [],
  offerTimeline: [],
  applyEmails: [],
  offer: {} as IApplicantOfferWithUIState,
  requirementsConditionTexts: {},
  courses: [],
  offerCreated: false,
  intakes: [],
  loadingIntakes: false,
  applicantIntakes: [],
  loadingCourses: false,
  loadingOffer: false,
  agentAccessStatus: null,
  identityDocuments: [],
  loadingIdentityDocuments: false,
  sectionDocuments: {},
  loadingSectionDocuments: {},
  documentCounts: {},
  allDocuments: {},
  loadingAllDocuments: false,
  universityIds: [],
  applicantUpdateSuccess: false,
  registration: {
    agent: {
      showSuccessfullRegisterToast: false,
      submittingRegisterApplicant: false,
      registerApplicantError: '',
    },
    uni: {
      showSuccessfullRegisterToast: false,
      submittingRegisterApplicant: false,
      registerApplicantError: '',
    },
    partner: {
      showSuccessfullRegisterToast: false,
      submittingRegisterApplicant: false,
      registerApplicantError: '',
    },
  },
  routeStatus: {
    sections: null,
    defaultTask: null,
    isLoading: false,
    error: undefined,
    loadedForApplicant: undefined,
    loadedForApplication: undefined,
  },
  calendlySchedulingInfos: {},
};

const reducer: Reducer<IApplyState> = (state = initialState, action) => {
  switch (action.type) {
    case ApplyActionTypes.GET_APPLICATION_RECORD: {
      return {
        ...state,
        offer: undefined,
        offers: initialState.offers,
        additionalRequirements: initialState.additionalRequirements,
        requirementsConditionTexts: initialState.requirementsConditionTexts,
        identityDocuments: initialState.identityDocuments,
        sectionDocuments: initialState.sectionDocuments,
        documentCounts: initialState.documentCounts,
        allDocuments: initialState.allDocuments,
        routeStatus: initialState.routeStatus,
        offerError: undefined,
        offerErrorForOfferId: undefined,
        loadingOffer: true,
        loadingOfferId: undefined,
      };
    }
    case ApplyActionTypes.GET_APPLICATION_RECORD_SUCCESS: {
      return {
        ...state,
        applicant: action.payload.applicant,
        application: action.payload.application,
        offers: action.payload.offers,
        documentUrls: action.payload.documentUrls,
        agentAccessStatus: action.payload.agentAccessStatus,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_OFFER: {
      return {
        ...state,
        loadingOffer: true,
        loadingOfferId: action.payload.offerId,
        offerError: undefined,
        offerErrorForOfferId: undefined,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_OFFER_SUCCESS: {
      return {
        ...state,
        offer: action.payload.offer,
        additionalRequirements: action.payload.additionalRequirements,
        requirementsConditionTexts: action.payload.requirementsConditionTexts,
        loadingOffer: false,
        loadingOfferId: undefined,
        offerError: undefined,
        offerErrorForOfferId: undefined,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_OFFER_FAILURE: {
      return {
        ...state,
        offer: undefined,
        additionalRequirements: initialState.additionalRequirements,
        requirementsConditionTexts: initialState.requirementsConditionTexts,
        loadingOffer: false,
        offerError: typeof action.payload === 'string' ? action.payload : 'Failed to load offer',
        offerErrorForOfferId: state.loadingOfferId,
        loadingOfferId: undefined,
      };
    }
    case ApplyActionTypes.GET_APPLY_INTAKES: {
      return {
        ...state,
        loadingIntakes: true,
      };
    }
    case ApplyActionTypes.GET_APPLY_INTAKES_SUCCESS: {
      return {
        ...state,
        intakes: action.payload.intakes,
        loadingIntakes: false,
      };
    }
    case ApplyActionTypes.GET_APPLY_INTAKES_FAILURE: {
      return {
        ...state,
        loadingIntakes: false,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_INTAKES: {
      return {
        ...state,
        applicantIntakes: [], // Clear old intakes to prevent showing stale data
        loadingApplicantIntakes: true,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_INTAKES_SUCCESS: {
      return {
        ...state,
        applicantIntakes: action.payload.applicantIntakes,
        loadingApplicantIntakes: false,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_INTAKES_FAILURE: {
      return {
        ...state,
        loadingApplicantIntakes: false,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_COURSES: {
      return {
        ...state,
        loadingCourses: true,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_COURSES_SUCCESS: {
      return {
        ...state,
        courses: action.payload.courses,
        loadingCourses: false,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_COURSES_FAILURE: {
      return {
        ...state,
        loadingCourses: false,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_COURSE_CHANGE_SUCCESS: {
      return {
        ...state,
        courseDetails: { programme: action.payload.programme, requirements: action.payload.requirements },
      };
    }
    case ApplyActionTypes.GET_APPLY_AUDIT_LOG_SUCCESS: {
      return {
        ...state,
        applyAuditLogs: action.payload.auditLogs,
      };
    }
    case ApplyActionTypes.GET_OFFER_TIMELINE_SUCCESS: {
      return {
        ...state,
        offerTimeline: action.payload.timeline,
      };
    }
    case ApplyActionTypes.GET_APPLY_EMAILS_SUCCESS: {
      return {
        ...state,
        applyEmails: action.payload.emails,
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_STATUS_SUCCESS: {
      const updatedTask = action.payload as IApplicantOfferRequirementTask;
      // Update task within hierarchical requirements structure
      const updatedOffer = {
        ...state.offer,
        requirements: updateTaskInRequirements(state.offer.requirements, updatedTask),
      };

      const updatedOffers = state.offers.map(offerItem => {
        if (offerItem.id === state.offer.id) {
          return {
            ...offerItem,
            requirements: updateTaskInRequirements(offerItem.requirements ?? [], updatedTask),
          };
        }
        return offerItem;
      });

      return {
        ...state,
        offer: updatedOffer,
        offers: updatedOffers,
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_LEVEL_SUCCESS: {
      const updatedTask = action.payload as IApplicantOfferRequirementTask;
      const updatedOffer = {
        ...state.offer,
        requirements: updateTaskInRequirements(state.offer.requirements, updatedTask),
      };

      const updatedOffers = state.offers.map(offerItem => {
        if (offerItem.id === state.offer.id) {
          return {
            ...offerItem,
            requirements: updateTaskInRequirements(offerItem.requirements ?? [], updatedTask),
          };
        }
        return offerItem;
      });

      return {
        ...state,
        offer: updatedOffer,
        offers: updatedOffers,
      };
    }
    case ApplyActionTypes.REQUEST_TASKS: {
      return {
        ...state,
        offer: {
          ...state.offer,
          changeStatusLoading: true,
        },
      };
    }
    case ApplyActionTypes.REQUEST_TASKS_SUCCESS: {
      const updatedOffer = action.payload as IApplicantOffer;
      return {
        ...state,
        offer: {
          ...state.offer,
          changeStatusLoading: false,
          ...updatedOffer,
        },
      };
    }
    case ApplyActionTypes.REQUEST_TASKS_FAILURE: {
      return {
        ...state,
        offer: {
          ...state.offer,
          changeStatusLoading: false,
        },
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_OFFER_STATUS: {
      return {
        ...state,
        offer: {
          ...state.offer,
          changeStatusLoading: true,
        },
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_OFFER_STATUS_SUCCESS: {
      const updatedOffer = action.payload as IApplicantOffer;

      const updatedOffers = state.offers.map(offerItem =>
        offerItem.id === updatedOffer.id
          ? {
              ...offerItem,
              status: updatedOffer.status,
            }
          : offerItem,
      );

      return {
        ...state,
        offers: updatedOffers,
        offer: {
          ...state.offer,
          changeStatusLoading: false,
          ...updatedOffer,
        },
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_PROGRAMME_SUCCESS: {
      const updatedOffer = action.payload as IApplicantOffer;

      // Update the offers array to keep it in sync
      const updatedOffers = state.offers.map(offerItem =>
        offerItem.id === updatedOffer.id
          ? {
              ...offerItem,
              ...updatedOffer,
            }
          : offerItem,
      );

      return {
        ...state,
        offer: {
          ...state.offer,
          ...updatedOffer,
        },
        offers: updatedOffers,
      };
    }
    case ApplyActionTypes.CREATE_APPLICANT_OFFER_SUCCESS: {
      return {
        ...state,
        offers: [...state.offers, action.payload],
        offer: action.payload,
        offerCreated: true,
      };
    }
    case ApplyActionTypes.RESET_OFFER_CREATED: {
      return {
        ...state,
        offerCreated: false,
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_OFFER_STATUS_FAILURE: {
      return {
        ...state,
        offer: {
          ...state.offer,
          changeStatusLoading: false,
        },
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_OFFER_LETTER_TEXT_SUCCESS: {
      return {
        ...state,
        offer: {
          ...state.offer,
          ...action.payload,
        },
      };
    }
    case ApplyActionTypes.CREATE_ADDITIONAL_REQUIREMENTS_SUCCESS: {
      return {
        ...state,
        offer: {
          ...state.offer,
          requirements: action.payload.requirements,
        },
        requirementsConditionTexts: action.payload.requirementsConditionTexts,
      };
    }

    /**
     * Registration action reducers
     */

    // clears all registration messages for all users
    case ApplyActionTypes.CLEAR_SUCCESSFULL_REGISTER_APPLICANT_TOAST_MESSAGE: {
      return {
        ...state,
        registration: {
          ...state.registration,
          agent: { ...state.registration.agent, showSuccessfullRegisterToast: false },
          uni: { ...state.registration.uni, showSuccessfullRegisterToast: false },
          partner: { ...state.registration.partner, showSuccessfullRegisterToast: false },
        },
      };
    }

    case ApplyActionTypes.AGENT_REGISTER_APPLICANT: {
      return {
        ...state,
        registration: {
          ...state.registration,
          agent: {
            ...state.registration.agent,
            submittingRegisterApplicant: true,
            registerApplicantError: undefined,
          },
        },
      };
    }
    case ApplyActionTypes.AGENT_REGISTER_APPLICANT_SUCCESS: {
      return {
        ...state,
        lastRegisteredApplicant: action.payload,
        registration: {
          ...state.registration,
          agent: {
            ...state.registration.agent,
            submittingRegisterApplicant: false,
            showSuccessfullRegisterToast: true,
            registerApplicantError: undefined,
          },
        },
      };
    }
    case ApplyActionTypes.AGENT_REGISTER_APPLICANT_FAILURE: {
      return {
        ...state,
        registration: {
          ...state.registration,
          agent: {
            ...state.registration.agent,
            submittingRegisterApplicant: false,
            registerApplicantError: action.payload,
          },
        },
      };
    }

    // Uni registration reducer actions and state variables are reused from agent registration; they hold the same information

    case ApplyActionTypes.UNI_REGISTER_APPLICANT: {
      return {
        ...state,
        registration: {
          ...state.registration,
          uni: {
            ...state.registration.uni,
            submittingRegisterApplicant: true,
            registerApplicantError: undefined,
          },
        },
      };
    }
    case ApplyActionTypes.UNI_REGISTER_APPLICANT_SUCCESS: {
      return {
        ...state,
        lastRegisteredApplicant: action.payload,
        registration: {
          ...state.registration,
          uni: {
            ...state.registration.uni,
            submittingRegisterApplicant: false,
            showSuccessfullRegisterToast: true,
            registerApplicantError: undefined,
          },
        },
      };
    }
    case ApplyActionTypes.UNI_REGISTER_APPLICANT_FAILURE: {
      return {
        ...state,
        registration: {
          ...state.registration,
          uni: {
            ...state.registration.uni,
            submittingRegisterApplicant: false,
            registerApplicantError: action.payload,
          },
        },
      };
    }

    case ApplyActionTypes.PARTNER_REGISTER_APPLICANT: {
      return {
        ...state,
        registration: {
          ...state.registration,
          partner: {
            ...state.registration.partner,
            submittingRegisterApplicant: true,
            registerApplicantError: undefined,
          },
        },
      };
    }
    case ApplyActionTypes.PARTNER_REGISTER_APPLICANT_SUCCESS: {
      return {
        ...state,
        lastRegisteredApplicant: action.payload,
        registration: {
          ...state.registration,
          partner: {
            ...state.registration.partner,
            submittingRegisterApplicant: false,
            showSuccessfullRegisterToast: true,
            registerApplicantError: undefined,
          },
        },
      };
    }
    case ApplyActionTypes.PARTNER_REGISTER_APPLICANT_FAILURE: {
      return {
        ...state,
        registration: {
          ...state.registration,
          partner: {
            ...state.registration.partner,
            submittingRegisterApplicant: false,
            registerApplicantError: action.payload,
          },
        },
      };
    }

    // ----

    case ApplyActionTypes.STUDENTS_TAGS_PIN_SUCCESS: {
      const tags = state.offer.tags?.length ? state.offer.tags : [];
      const updatedOffer = {
        ...state.offer,
        tags: [...tags, action.payload.tag],
      };
      return {
        ...state,
        offer: { ...updatedOffer },
      };
    }
    case ApplyActionTypes.STUDENTS_TAGS_UNPIN_SUCCESS: {
      const filterdTags = state?.offer?.tags?.filter(({ id }) => id !== action.payload.tag.id);
      const updatedOffer = {
        ...state.offer,
        tags: filterdTags,
      };
      const updatedOffers = state?.offers.map(offer =>
        offer.id === action.payload.offerId
          ? { ...offer, tags: offer?.tags?.filter(tag => tag.id !== action.payload.tag.id) }
          : offer,
      );
      return {
        ...state,
        offers: updatedOffers,
        offer: updatedOffer,
      };
    }
    case ApplyActionTypes.UNLINK_APPLICANT_EMAIL_SUCCESS: {
      return {
        ...state,
        applicant: { ...state.applicant, email: EmailStatus.NoEmail },
        application: { ...state.application, email: EmailStatus.NoEmail },
      };
    }

    case ApplyActionTypes.ASSIGN_APPLICANT_TO_AGENT_SUCCESS: {
      return {
        ...state,
        application: action.payload.application,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_DOCUMENTS: {
      return {
        ...state,
        loadingIdentityDocuments: true,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_DOCUMENTS_SUCCESS: {
      return {
        ...state,
        identityDocuments: action.payload.documents,
        loadingIdentityDocuments: false,
      };
    }
    case ApplyActionTypes.GET_APPLICANT_DOCUMENTS_FAILURE: {
      return {
        ...state,
        loadingIdentityDocuments: false,
      };
    }

    case ApplyActionTypes.GET_ALL_DOCUMENTS_WITH_COUNTS: {
      return {
        ...state,
        identityDocuments: initialState.identityDocuments,
        sectionDocuments: initialState.sectionDocuments,
        documentCounts: initialState.documentCounts,
        allDocuments: initialState.allDocuments,
        loadingAllDocuments: true,
      };
    }
    case ApplyActionTypes.GET_ALL_DOCUMENTS_WITH_COUNTS_SUCCESS: {
      const { documentsWithCounts } = action.payload;
      const documentCounts = {};
      const allDocuments = {};

      for (const { docType, documents, count } of documentsWithCounts) {
        documentCounts[docType] = count;
        allDocuments[docType] = documents;
      }

      return {
        ...state,
        documentCounts,
        allDocuments,
        loadingAllDocuments: false,
      };
    }
    case ApplyActionTypes.GET_ALL_DOCUMENTS_WITH_COUNTS_FAILURE: {
      return {
        ...state,
        loadingAllDocuments: false,
      };
    }
    case ApplyActionTypes.SELECT_STUDIES_TO_ASSESS_SUCCESS: {
      return {
        ...state,
        selectStudiesToAssessSavedAt: Date.now(),
      };
    }
    case ApplyActionTypes.EXEMPT_APPLICANT_REQUIREMENT_SUCCESS: {
      const updatedOffer = action.payload as IApplicantOffer;
      const updatedOffers = state.offers.map(offerItem =>
        offerItem.id === updatedOffer.id ? { ...offerItem, ...updatedOffer } : offerItem,
      );
      return {
        ...state,
        offers: updatedOffers,
        offer: {
          ...state.offer,
          ...updatedOffer,
        },
      };
    }
    case ApplyActionTypes.EXEMPT_APPLICANT_OFFER_PAYMENT_SUCCESS: {
      return {
        ...state,
        offer: {
          ...state.offer,
          payment: action.payload,
        },
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_DEPOSIT_STATUS_SUCCESS: {
      return {
        ...state,
        offer: {
          ...state.offer,
          payment: action.payload,
        },
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_PROOF_STATUS_SUCCESS: {
      return {
        ...state,
        offer: {
          ...state.offer,
          payment: action.payload,
        },
      };
    }
    case ApplyActionTypes.DELETE_APPLICANT_OFFER_PROOF_OF_PAYMENT_FILE_SUCCESS: {
      return {
        ...state,
        offer: {
          ...state.offer,
          payment: action.payload,
        },
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_DETAILS: {
      return {
        ...state,
        isUpdatingApplicantDetails: true,
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_DETAILS_SUCCESS: {
      const updatedData = {
        ...action.payload.data,
        highestQualification: {
          code: action.payload.data.highestQualificationCode,
        },
      };
      return {
        ...state,
        applicant: {
          ...state.applicant,
          ...updatedData,
        },
        application: {
          ...state.application,
          ...updatedData,
        },
        updatedApplicationUserId: action.payload.applicantId,
        isUpdatingApplicantDetails: false,
        applicantUpdateSuccess: true,
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_DETAILS_FAILURE: {
      return {
        ...state,
        isUpdatingApplicantDetails: false,
      };
    }
    case ApplyActionTypes.UPDATE_APPLICANT_DETAILS_RESET: {
      return {
        ...state,
        updatedApplicationUserId: undefined,
        applicantUpdateSuccess: false,
      };
    }
    case ApplyActionTypes.GET_APPLY_UNI_LIST_SUCCESS: {
      return {
        ...state,
        universityIds: action.payload.universityIds,
      };
    }
    case ApplyActionTypes.GET_ROUTE_STATUS: {
      return {
        ...state,
        routeStatus: {
          ...state.routeStatus,
          isLoading: true,
          error: undefined,
        },
      };
    }
    case ApplyActionTypes.GET_ROUTE_STATUS_SUCCESS: {
      return {
        ...state,
        routeStatus: {
          sections: action.payload.sections,
          defaultTask: action.payload.defaultTask,
          isLoading: false,
          error: undefined,
          loadedForApplicant: action.payload.applicantId,
          loadedForApplication: action.payload.applicationId,
        },
      };
    }
    case ApplyActionTypes.GET_ROUTE_STATUS_FAILURE: {
      return {
        ...state,
        routeStatus: {
          ...state.routeStatus,
          isLoading: false,
          error: action.payload,
        },
      };
    }

    case ApplyActionTypes.UPDATE_DUPLICATE: {
      return {
        ...state,
        updateDuplicateResponse: undefined,
      };
    }
    case ApplyActionTypes.UPDATE_DUPLICATE_SUCCESS: {
      return {
        ...state,
        updateDuplicateResponse: action.payload,
      };
    }
    case ApplyActionTypes.UPDATE_DUPLICATE_FAILURE: {
      return {
        ...state,
        updateDuplicateResponse: false,
        updateDuplicateError: action.payload,
      };
    }
    case ApplyActionTypes.GET_CALENDLY_SCHEDULING_INFO: {
      const existing = state.calendlySchedulingInfos[action.payload.eventType];
      // Skip if already loading or if we have cached data — prevents concurrent fetches and unnecessary re-fetches
      if (existing?.isLoading || existing?.data != null) return state;
      return {
        ...state,
        calendlySchedulingInfos: {
          ...state.calendlySchedulingInfos,
          [action.payload.eventType]: { isLoading: true, error: undefined, data: existing?.data ?? null },
        },
      };
    }
    case ApplyActionTypes.GET_CALENDLY_SCHEDULING_INFO_SUCCESS: {
      const { eventType, ...data } = action.payload;
      return {
        ...state,
        calendlySchedulingInfos: {
          ...state.calendlySchedulingInfos,
          [eventType]: { isLoading: false, data },
        },
      };
    }
    case ApplyActionTypes.GET_CALENDLY_SCHEDULING_INFO_FAILURE: {
      const existing = state.calendlySchedulingInfos[action.payload.eventType];
      return {
        ...state,
        calendlySchedulingInfos: {
          ...state.calendlySchedulingInfos,
          [action.payload.eventType]: { isLoading: false, error: action.payload.error, data: existing?.data ?? null },
        },
      };
    }
    default: {
      return state;
    }
  }
};

export { reducer as applyReducer };
